// BackupMenu.tsx — header icon button that opens the Back up / Restore panel. Moved out of the
// app's main body 2026-08-02 (Bogdan: this isn't the central focus of the app, it's a utility —
// belongs next to the account avatar, not at the top of the shows list). Mirrors AccountMenu.tsx's
// popover shape exactly (icon button → absolutely-positioned card + backdrop), just holding
// <CloudBackup/> plus the export/privacy bits that used to sit in the "Your rounds" toolbar.
import { useRef, useState } from "react";
import { CloudBackup as CloudBackupIcon, Lock } from "lucide-react";
import { CloudBackup } from "./CloudBackup";

export function BackupMenu({
  appName, fileSlug, appScheme, hasData, onRestored, onExportCsv,
}: {
  appName: string;
  fileSlug: string;
  appScheme: string;
  hasData: boolean;
  onRestored: () => void | Promise<void>;
  /** Optional — apps that offer a CSV export render its button inside this panel too. */
  onExportCsv?: () => void;
}) {
  const [open, setOpen] = useState(false);
  // The panel is anchored `right: 0` to this toggle, so its LEFT edge lands at
  // (toggle.right − width). `min(320px, 88vw)` only clamps against the VIEWPORT, never against
  // how much room is actually left of the toggle — with the account avatar to its right, 320px
  // hung off the left edge of a 375pt phone, clipping the card's border and rounding (measured
  // live 2026-08-28: {left:-3, right:317, width:320, vw:375}). Fourth app this week with the
  // identical defect in this byte-identical component (Hivewise → Chores → Stash → Encore);
  // Encore's is the mildest only because its header carries two controls instead of four.
  // Measure the room at open time and cap on it. `null` before the first open = the old value,
  // so nothing changes on a wide screen (430 stays 320px, measured).
  const btnRef = useRef<HTMLButtonElement>(null);
  const [room, setRoom] = useState<number | null>(null);
  function toggle() {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setRoom(Math.max(220, Math.round(r.right) - 8)); // 8px breathing room at the screen edge
    setOpen((o) => !o);
  }
  return (
    <div style={{ position: "relative" }}>
      <button ref={btnRef} aria-label="Back up & restore" onClick={toggle}
        style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--card-bg, #fff)", border: "1px solid var(--border)", display: "grid", placeItems: "center", cursor: "pointer" }}>
        <CloudBackupIcon size={16} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="card" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, width: room ? `min(320px, ${room}px)` : "min(320px, 88vw)", padding: "0.9rem", textAlign: "left", maxHeight: "75vh", overflowY: "auto" }}>
            <div style={{ fontSize: "var(--fs-sm)", fontWeight: 700, marginBottom: "0.6rem" }}>Back up & restore</div>
            <CloudBackup appName={appName} fileSlug={fileSlug} appScheme={appScheme} hasData={hasData} onRestored={onRestored} />
            {onExportCsv && (
              <div style={{ marginTop: "0.7rem", paddingTop: "0.6rem", borderTop: "1px solid var(--border)" }}>
                <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: "0.15rem" }}>Export CSV</div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.4rem" }}>
                  A spreadsheet of your shows (date, artist, venue, city, notes) — for viewing or printing, not a re-importable backup.
                </div>
                <button className="btn btn-sm" style={{ width: "100%" }} onClick={onExportCsv}>Export CSV</button>
              </div>
            )}
            <div className="chip" style={{ gap: "0.25rem", marginTop: "0.6rem", width: "fit-content" }}>
              <Lock size={11} /> Your data stays on this device unless you back it up above
            </div>
          </div>
        </>
      )}
    </div>
  );
}
